import { FormControlLabel } from '@fox/atoms/src/form-composites/form-control';
import { MuiRadio, MuiRadioGroup } from '@fox/atoms/src/mui-radio/index';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { Typography } from '@fox/atoms/src/typography/index';
import { SpaceBetweenRow } from '@fox/layout/src/flex';
import { CREATE_PEOPLE_BLOCK } from '../../../../form-block-types';
import { IField, IQuestionAnswer } from '../../../form-root/contracts';
import { useFormRuntimeContext } from '../../../form-runtime-context/form-runtime-context';
import { QuestionLabelRender } from '../../question-block/question-label';
import { BUSINESS_ADDRESS_FIELD, BUSINESS_NAME_FIELD, CreatePersonBlockMetadata } from '../contracts';
import { AddressChange } from '../shared-components/fields';
import { QuestionField } from '../shared-components/question-field';
import { getRenderField } from '../shared-components/render-fields';
import { updateBusinessDetailsForAllContacts } from '../util';

export const BusinessDetailsSection = ({
	id,
	currentAnswer,
	props
}: {
	id: string;
	currentAnswer: IQuestionAnswer;
	props: IField & { businessDetailsName?: string };
}) => {
	const { answerQuestion } = useFormRuntimeContext();
	const fieldProps = props;
	const metaData: CreatePersonBlockMetadata = JSON.parse(fieldProps.metadataJson ?? '{}');
	const addressKeys = Object.keys(metaData.editFields ?? {}).filter(k => k.startsWith(BUSINESS_ADDRESS_FIELD));
	const currentAnswerSelections = tryParseJSONObject(currentAnswer?.answer?.answerJson);

	return (
		<>
			<QuestionLabelRender
				id={id}
				style={{ fontSize: '1.25rem', padding: 0 }}
				value={fieldProps.businessDetailsName ?? 'Organisation details'}
			/>
			<QuestionField
				id={id}
				required={metaData.editFields?.businessName?.meta?.required}
				tabIndex={undefined}
				description="Organisation name"
				value={currentAnswerSelections?.['businessDetails']?.[BUSINESS_NAME_FIELD] ?? ''}
				onChange={c => {
					// needs to sometimes just update business details
					updateBusinessDetailsForAllContacts(
						currentAnswer,
						currentAnswerSelections,
						answerQuestion,
						[BUSINESS_NAME_FIELD],
						[c],
						currentAnswerSelections?.['businessDetails']?.allContactsFromBusiness,
						true,
						CREATE_PEOPLE_BLOCK
					);
				}}
				component={getRenderField('businessName')}
			/>
			{addressKeys.length > 0
				? addressKeys.map(a => {
						const field = metaData?.editFields?.[a];
						if (!field) {
							return <></>;
						}
						return (
							<QuestionField
								id={a}
								required={field.meta.required}
								tabIndex={undefined}
								description={field.labelOverride ?? field.label}
								value={currentAnswerSelections?.['businessDetails']?.[a]}
								onChange={c => {
									updateBusinessDetailsForAllContacts(
										currentAnswer,
										currentAnswerSelections,
										answerQuestion,
										[a],
										[c],
										currentAnswerSelections?.['businessDetails']?.allContactsFromBusiness,
										true,
										CREATE_PEOPLE_BLOCK
									);
								}}
								component={AddressChange}
							/>
						);
					})
				: null}
			{currentAnswerSelections?.contacts?.length > 1 ? (
				<SpaceBetweenRow>
					<Typography sx={{ fontWeight: 700 }}>
						{currentAnswerSelections.contacts.length < 2
							? 'Is contact from this organisation'
							: 'Are all contacts from this organisation?'}
					</Typography>
					<MuiRadioGroup row value={currentAnswerSelections?.['businessDetails']?.['allContactsFromBusiness'] ?? true}>
						<FormControlLabel
							control={
								<MuiRadio
									sx={{
										'& .MuiSvgIcon-root': {
											fontSize: '1rem'
										}
									}}
									value={true}
									onClick={() => {
										updateBusinessDetailsForAllContacts(
											currentAnswer,
											currentAnswerSelections,
											answerQuestion,
											[BUSINESS_NAME_FIELD, ...addressKeys],
											[
												currentAnswerSelections?.businessDetails?.businessName ?? '',
												...addressKeys.map(k => currentAnswerSelections?.businessDetails?.[k] ?? '')
											],
											true,
											false,
											CREATE_PEOPLE_BLOCK
										);
									}}
								/>
							}
							label="Yes"
						/>
						<FormControlLabel
							control={
								<MuiRadio
									sx={{
										'& .MuiSvgIcon-root': {
											fontSize: '1rem'
										}
									}}
									value={false}
									onClick={() => {
										updateBusinessDetailsForAllContacts(
											currentAnswer,
											currentAnswerSelections,
											answerQuestion,
											[BUSINESS_NAME_FIELD, 'asAbove', ...addressKeys],
											[undefined, false, ...addressKeys.map(_ => undefined)],
											false,
											false,
											CREATE_PEOPLE_BLOCK
										);
									}}
								/>
							}
							label="No"
						/>
					</MuiRadioGroup>
				</SpaceBetweenRow>
			) : null}
		</>
	);
};
